<template>
    <div v-if="isOpen" class="modal-overlay">
        <div class="modal">
            <div class="modal-header">
                <h3 class="text-2xl font-bold">{{ $translate('Add another attendee to this Event!') }}</h3>
                <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            
            <p class="text-gray-600 mb-6">
                {{ $translate('Please select an attendee from the options below or add a new one.') }}
            </p>
            
            <div class="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-md">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-blue-700">
                            <span class="font-medium">TIP:</span> Pokud přidáte jiného účastníka můžete se z události odstranit, tímto způsobem jako např. asistentka můžete zaplatit za někoho jiného
                        </p>
                    </div>
                </div>
            </div>
            
            <form>
                <!-- Select from Attendees -->
                <div class="mb-5">
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $translate('Select from Attendees') }}</label>
                    <div class="relative">
                        <select v-model="selectedAttendee" class="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm appearance-none">
                            <option value="">{{ $translate('Select an attendee') }}</option>
                            <option v-for="attendee in attendees" :key="attendee.id" :value="attendee.id">
                                {{ attendee.data.full_name }} ({{ attendee.data.email }})
                            </option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                            <svg class="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                        </div>
                    </div>
                </div>
                
                <!-- Other Members of the Organization -->
                <div class="mb-5">
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $translate('Other members of the organization') }}</label>
                    <div class="relative">
                        <select v-model="selectedInvite" class="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm appearance-none">
                            <option value="">{{ $translate('Select member from organization') }}</option>
                            <option v-for="invite in invites" :key="invite.id" :value="invite.id">
                                {{ invite.data.full_name }} ({{ invite.data.email }})
                            </option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                            <svg class="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                        </div>
                    </div>
                </div>
                
                <div class="separator">
                    <span>{{ $translate('Or add new contact') }}</span>
                </div>
                
                <!-- Manual Contact Entry -->
                <div class="grid grid-cols-2 gap-4 mb-5">
                    <div>
                        <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">{{ $translate('First Name') }}</label>
                        <input type="text" v-model="manualContact.first_name" id="first_name"
                            class="block w-full px-3 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm" />
                    </div>
                    <div>
                        <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">{{ $translate('Last Name') }}</label>
                        <input type="text" v-model="manualContact.last_name" id="last_name"
                            class="block w-full px-3 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm" />
                    </div>
                </div>
                
                <div class="mb-6">
                    <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ $translate('Email') }}</label>
                    <input type="email" v-model="manualContact.email" id="email"
                        class="block w-full px-3 py-3 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm" />
                </div>
                
                <!-- Submit and Cancel Buttons -->
                <div class="flex justify-end space-x-3">
                    <button type="button" @click="closeModal"
                        class="px-5 py-3 bg-gray-100 text-gray-800 font-medium rounded-lg hover:bg-gray-200 transition-colors">
                        {{ $translate('Cancel') }}
                    </button>
                    <button type="button" @click="submitContact"
                        class="px-5 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                        <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ $translate('Submit') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue';
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import { translate } from '@/utils/translate';

export default {
    name: 'ContactModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true
        },
        itemId: {
            type: String,
            required: false
        }
    },
    setup(props, { emit }) {
        const manualContact = ref({
            first_name: '',
            last_name: '',
            email: ''
        });

        const attendees = ref([]);
        const invites = ref([]);
        const selectedAttendee = ref('');
        const selectedInvite = ref('');
        const isSubmitting = ref(false);

        const fetchContacts = async () => {
            try {
                const response = await fetch(
                    `${window.vueAppConfig.endpoint}/api/v2/events/cart/contact/list`,
                    {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            brand_id: window.vueAppConfig.info.id,
                            session_id: cookie(),
                            item_id: props.itemId
                        })
                    }
                );
                const data = await response.json();
                if (data.status) {
                    attendees.value = data.attendee;
                    invites.value = data.invite;
                }
            } catch (error) {
                console.error('Error fetching contacts:', error);
            }
        };

        const submitContact = () => {
            if (isSubmitting.value) return;
            isSubmitting.value = true;
            console.log('submitContact called');

            // Check for selected attendee
            if (selectedAttendee.value) {
                const selectedContact = attendees.value.find(a => a.id === selectedAttendee.value);
                if (selectedContact) {
                    emit('submit', { id: selectedContact.id, type: 'attendee' });
                    isSubmitting.value = false;
                    return;
                }
            }

            // Check for selected invitee
            if (selectedInvite.value) {
                const selectedContact = invites.value.find(i => i.id === selectedInvite.value);
                if (selectedContact) {
                    emit('submit', { id: selectedContact.id, type: 'invite' });
                    isSubmitting.value = false;
                    return;
                }
            }

            // Check for manual contact entry
            if (manualContact.value.first_name && manualContact.value.last_name && manualContact.value.email) {
                emit('submit', { ...manualContact.value, type: 'new_contact' });
                isSubmitting.value = false;
                return;
            }

            // If none selected, prompt the user
            notify({
                title: translate('Error'),
                text: translate('Please select an attendee or invitee or enter a new contact.'),
                type: 'error',
                group: 'error'
            });
            isSubmitting.value = false;
        };

        const closeModal = () => {
            emit('close');
        };

        watch(props, (newProps) => {
            if (newProps.isOpen) {
                fetchContacts();
                // Reset all fields
                manualContact.value = {
                    first_name: '',
                    last_name: '',
                    email: ''
                };
                selectedAttendee.value = '';
                selectedInvite.value = '';
            }
        });

        return {
            manualContact,
            attendees,
            invites,
            selectedAttendee,
            selectedInvite,
            isSubmitting,
            submitContact,
            closeModal
        };
    }
};
</script>


<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.modal {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    max-width: 32rem;
    width: 100%;
    margin: 1rem;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 1.5rem 0;
    color: #6b7280;
    font-size: 0.875rem;
}

.separator::before,
.separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e5e7eb;
}

.separator::before {
    margin-right: 0.75rem;
}

.separator::after {
    margin-left: 0.75rem;
}

/* Add smooth transitions */
button, 
input, 
select {
    transition: all 0.2s ease;
}

/* Input focus styles */
input:focus, 
select:focus {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>